import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { formatPhone } from '../../shared/phone';
import { formatShortDateTime } from '../../shared/time';
import {
  Button,
  Callout,
  EmptyState,
  IconExternal,
  IconLink,
  Pagination,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeletonRows,
  Workspace,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';

export interface CateringLink {
  id?: number;
  customer_id: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  catering_link: string;
  created_at: string;
}

export interface CateringLinksPage {
  links: CateringLink[];
  totalCount: number;
}

export const LINKS_PAGE_SIZE = 25;
export const DEFAULT_LINKS_ORDERING = '-created_at';
const SORTABLE = ['created_at', 'customer_id', 'customer_name'] as const;
type LinkSort = (typeof SORTABLE)[number];

/** GET /api/catering-links/?page=&page_size=&ordering= */
export async function fetchCateringLinks(
  page: number,
  ordering: string,
): Promise<CateringLinksPage> {
  const response = await api.get(
    apiEndpoints.getCateringLinks({
      page,
      pageSize: LINKS_PAGE_SIZE,
      ordering,
    }),
  );
  if (!response?.ok) throw new Error('Catering links could not be loaded.');
  const data = await response.json();
  if (Array.isArray(data)) return { links: data, totalCount: data.length };
  return {
    links: Array.isArray(data?.results) ? data.results : [],
    totalCount: data?.count ?? 0,
  };
}

export function useCateringLinks(page: number, ordering: string) {
  return useQuery({
    queryKey: useScopedQueryKey(
      'catering-links',
      page,
      LINKS_PAGE_SIZE,
      ordering,
    ),
    queryFn: () => fetchCateringLinks(page, ordering),
    placeholderData: keepPreviousData,
  });
}

const COLUMNS: { key: string; label: string; width?: number }[] = [
  { key: 'created_at', label: 'Sent', width: 168 },
  { key: 'customer_name', label: 'Customer', width: 220 },
  { key: 'customer_phone', label: 'Phone', width: 152 },
  { key: 'customer_id', label: 'Customer ID', width: 120 },
  { key: 'catering_link', label: 'Catering link' },
];

const Missing: React.FC = () => <span className="text-v2-fg-4">—</span>;

/** Catering ordering links Arthur texted to guests. */
export const CateringLinks: React.FC<{ sectionSwitch: React.ReactNode }> = ({
  sectionSwitch,
}) => {
  const [params, setParams] = useSearchParams();
  const ordering = params.get('ordering') || DEFAULT_LINKS_ORDERING;
  const sortField = ordering.replace(/^-/, '') as LinkSort;
  const sortDir = ordering.startsWith('-') ? 'desc' : 'asc';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const { data, isPending, isError, error, isFetching, refetch } =
    useCateringLinks(page, ordering);

  const links = data?.links ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / LINKS_PAGE_SIZE));

  const setParam = (patch: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(patch).forEach(([k, v]) =>
          v ? next.set(k, v) : next.delete(k),
        );
        return next;
      },
      { replace: true },
    );
  const sort = (field: LinkSort) => {
    const desc =
      sortField === field ? sortDir === 'asc' : field === 'created_at';
    const value = `${desc ? '-' : ''}${field}`;
    setParam({
      ordering: value === DEFAULT_LINKS_ORDERING ? null : value,
      page: null,
    });
  };

  return (
    <Workspace
      title="Catering"
      meta={
        data
          ? `${totalCount.toLocaleString()} ${totalCount === 1 ? 'link' : 'links'}`
          : undefined
      }
      actions={sectionSwitch}
      toolbar={
        <p className="flex h-11 items-center px-4 text-v2-12 text-v2-fg-3 lg:px-6">
          Ordering links Arthur texted to guests so they could place a catering
          order themselves.
        </p>
      }
    >
      {isError && !data ? (
        <div className="p-4 lg:p-6">
          <Callout tone="danger" title="Couldn’t load catering links">
            {(error as Error).message}
            <div className="mt-2">
              <Button size="sm" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          </Callout>
        </div>
      ) : !isPending && links.length === 0 ? (
        <EmptyState
          icon={<IconLink />}
          title="No catering links yet"
          description="When Arthur texts a guest a catering link, it shows up here."
        />
      ) : (
        <>
          <div
            className={clsx(
              'v2-scroll min-h-0 flex-1 overflow-auto transition-opacity',
              isFetching && !isPending && 'opacity-70',
            )}
          >
            <Table fixed className="min-w-[880px]">
              <THead>
                <tr>
                  {COLUMNS.map((c) => {
                    const sortable = (SORTABLE as readonly string[]).includes(
                      c.key,
                    );
                    return (
                      <TH
                        key={c.key}
                        width={c.width}
                        sortable={sortable}
                        active={sortable && sortField === c.key}
                        dir={sortDir}
                        onSort={() => sort(c.key as LinkSort)}
                      >
                        {c.label}
                      </TH>
                    );
                  })}
                </tr>
              </THead>
              <TBody>
                {isPending ? (
                  <TableSkeletonRows cols={COLUMNS.length} />
                ) : (
                  links.map((link, i) => (
                    <TR
                      key={
                        link.id ?? `${link.customer_id}-${link.created_at}-${i}`
                      }
                    >
                      <TD num className="whitespace-nowrap">
                        {formatShortDateTime(link.created_at) || <Missing />}
                      </TD>
                      <TD truncate className="font-medium">
                        {link.customer_name || <Missing />}
                      </TD>
                      <TD num className="whitespace-nowrap text-v2-fg-2">
                        {formatPhone(link.customer_phone) || <Missing />}
                      </TD>
                      <TD num className="text-v2-12 text-v2-fg-3">
                        {link.customer_id ?? <Missing />}
                      </TD>
                      <TD className="overflow-hidden">
                        {/^https?:\/\//i.test(link.catering_link) ? (
                          <div className="flex min-w-0 items-center gap-3">
                            <a
                              href={link.catering_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex shrink-0 items-center gap-1 font-medium text-v2-accent hover:underline"
                            >
                              Open link
                              <IconExternal size={12} />
                            </a>
                            <span
                              className="truncate text-v2-12 text-v2-fg-4"
                              title={link.catering_link}
                            >
                              {link.catering_link}
                            </span>
                          </div>
                        ) : (
                          <span className="block truncate text-v2-12 text-v2-fg-2">
                            {link.catering_link}
                          </span>
                        )}
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </div>
          {data && totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={LINKS_PAGE_SIZE}
              onPage={(p) => setParam({ page: p > 1 ? String(p) : null })}
              noun="links"
            />
          )}
        </>
      )}
    </Workspace>
  );
};
